import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import toast from 'react-hot-toast'
import { apiFetch } from '../lib/api'

export interface WatchlistItem {
  id: number
  user_id: number
  symbol: string
  name: string
  port_api_type: 'us_stock' | 'th_stock'
  notes?: string
  created_at: string
  updated_at: string
}

export interface WatchlistPrice {
  price: number
  change: number
  changePercent: number
  currency: string
  high52w: number | null
  low52w: number | null
  marketCap: number | null
  volume: number | null
  avgVolume: number | null
  pe: number | null
  forwardPE: number | null
  eps: number | null
  dividendYield: number | null
  dividendPerShare: number | null
  priceToBook: number | null
  fiftyDayAvg: number | null
  twoHundredDayAvg: number | null
  sector: string | null
}

export interface AnalysisHistory {
  symbol: string
  analysis: string
  generated_at: string
}

interface WatchlistStore {
  items: WatchlistItem[]
  prices: Record<string, WatchlistPrice>
  sparklines: Record<string, number[]>
  loading: boolean
  loaded: boolean
  pricesLoading: boolean
  sparklinesLoading: boolean
  analyzing: Record<string, boolean>
  analysisResults: Record<string, { text: string; generatedAt: string; fromCache?: boolean; streaming?: boolean }>
  analysisHistory: AnalysisHistory[]
  historyLoading: boolean

  loadWatchlist: (force?: boolean) => Promise<void>
  addItem: (symbol: string, name: string, port_api_type: 'us_stock' | 'th_stock') => Promise<void>
  removeItem: (id: number) => Promise<void>
  fetchPrices: () => Promise<void>
  fetchSparklines: () => Promise<void>
  analyzeStock: (symbol: string, item: WatchlistItem, force?: boolean) => Promise<void>
  clearAnalysis: (symbol: string) => void
  loadAnalysisHistory: () => Promise<void>
}

export const useWatchlistStore = create<WatchlistStore>()(
  devtools(
    (set, get) => ({
      items: [],
      prices: {},
      sparklines: {},
      loading: false,
      loaded: false,
      pricesLoading: false,
      sparklinesLoading: false,
      analyzing: {},
      analysisHistory: [],
      historyLoading: false,
      analysisResults: {},

      loadWatchlist: async (force = false) => {
        const { loaded, loading } = get()
        if (!force && loaded) return   // already fetched this session
        if (loading) return             // concurrent call in progress

        set({ loading: true })
        try {
          const res = await apiFetch('/api/watchlist')
          if (!res.ok) throw new Error('Failed to load watchlist')
          const { data } = await res.json()
          set({ items: data || [], loaded: true })
          if (data && data.length > 0) {
            get().fetchPrices()
            get().fetchSparklines()
          }
        } catch (error) {
          console.error('Error loading watchlist:', error)
          toast.error('ไม่สามารถโหลด Watchlist ได้')
        } finally {
          set({ loading: false })
        }
      },

      addItem: async (symbol, name, port_api_type) => {
        try {
          const res = await apiFetch('/api/watchlist', {
            method: 'POST',
            body: JSON.stringify({ symbol, name, port_api_type }),
          })
          if (!res.ok) {
            const err = await res.json()
            if (res.status === 409) throw new Error('หุ้นนี้อยู่ใน Watchlist แล้ว')
            throw new Error(err.error || 'Failed to add to watchlist')
          }
          const { data } = await res.json()
          set((state) => ({ items: [data, ...state.items] }))
          toast.success(`เพิ่ม ${symbol} เข้า Watchlist แล้ว`)
          get().fetchPrices()
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'เกิดข้อผิดพลาด'
          toast.error(msg)
          throw error
        }
      },

      removeItem: async (id) => {
        const item = get().items.find((i) => i.id === id)
        try {
          const res = await apiFetch(`/api/watchlist/${id}`, { method: 'DELETE' })
          if (!res.ok) throw new Error('Failed to remove from watchlist')
          set((state) => ({ items: state.items.filter((i) => i.id !== id) }))
          if (item) toast.success(`ลบ ${item.symbol} ออกจาก Watchlist แล้ว`)
        } catch (error) {
          toast.error('ไม่สามารถลบได้')
        }
      },

      fetchPrices: async () => {
        if (get().pricesLoading) return
        set({ pricesLoading: true })
        try {
          const res = await apiFetch('/api/watchlist/prices')
          if (!res.ok) throw new Error('Failed to fetch prices')
          const { prices } = await res.json()
          set({ prices: prices || {} })
        } catch (error) {
          console.error('Error fetching watchlist prices:', error)
        } finally {
          set({ pricesLoading: false })
        }
      },

      fetchSparklines: async () => {
        if (get().sparklinesLoading) return
        set({ sparklinesLoading: true })
        try {
          const res = await apiFetch('/api/watchlist/sparklines')
          if (!res.ok) throw new Error('Failed to fetch sparklines')
          const { sparklines } = await res.json()
          set({ sparklines: sparklines || {} })
        } catch (error) {
          console.error('Error fetching sparklines:', error)
        } finally {
          set({ sparklinesLoading: false })
        }
      },

      analyzeStock: async (symbol, item, force = false) => {
        set((state) => ({ analyzing: { ...state.analyzing, [symbol]: true } }))
        set((state) => ({
          analysisResults: {
            ...state.analysisResults,
            [symbol]: { text: '', generatedAt: '', streaming: true },
          },
        }))
        try {
          const price = get().prices[symbol]
          const res = await apiFetch(`/api/watchlist/${symbol}/analyze`, {
            method: 'POST',
            body: JSON.stringify({
              name: item.name,
              port_api_type: item.port_api_type,
              price: price?.price,
              change: price?.change,
              changePercent: price?.changePercent,
              high52w: price?.high52w,
              low52w: price?.low52w,
              force,
            }),
          })
          if (!res.ok) {
            const err = await res.json()
            if (res.status === 503) throw new Error('กรุณาตั้งค่า ANTHROPIC_API_KEY ก่อนใช้งาน')
            throw new Error(err.error || 'Analysis failed')
          }

          const reader = res.body?.getReader()
          if (!reader) throw new Error('No stream')
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue
              const payload = JSON.parse(line.slice(6))
              if (payload.error) throw new Error(payload.error)
              if (payload.text) {
                set((state) => ({
                  analysisResults: {
                    ...state.analysisResults,
                    [symbol]: {
                      ...state.analysisResults[symbol],
                      text: (state.analysisResults[symbol]?.text ?? '') + payload.text,
                    },
                  },
                }))
              }
              if (payload.done) {
                set((state) => ({
                  analysisResults: {
                    ...state.analysisResults,
                    [symbol]: {
                      ...state.analysisResults[symbol],
                      generatedAt: payload.generatedAt,
                      fromCache: false,
                      streaming: false,
                    },
                  },
                }))
              }
            }
          }
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'วิเคราะห์ไม่สำเร็จ'
          toast.error(msg)
          set((state) => {
            const next = { ...state.analysisResults }
            delete next[symbol]
            return { analysisResults: next }
          })
        } finally {
          set((state) => ({ analyzing: { ...state.analyzing, [symbol]: false } }))
        }
      },

      clearAnalysis: (symbol) => {
        set((state) => {
          const next = { ...state.analysisResults }
          delete next[symbol]
          return { analysisResults: next }
        })
      },

      loadAnalysisHistory: async () => {
        set({ historyLoading: true })
        try {
          const res = await apiFetch('/api/watchlist/analyses')
          if (!res.ok) throw new Error('Failed to load analysis history')
          const { analyses } = await res.json()
          set({ analysisHistory: analyses || [] })
        } catch (error) {
          console.error('Error loading analysis history:', error)
        } finally {
          set({ historyLoading: false })
        }
      },
    }),
    { name: 'watchlist-store' }
  )
)
